import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { Flex } from "@redotech/redo-web/flex";
import { TabsPortal } from "@redotech/redo-web/tabs-portal";
import { memo, useCallback, useState } from "react";

import { MarketingFlagKey } from "@redotech/feature-flags-keys-marketing/keys";
import type { AttributionEvidenceType } from "@redotech/redo-attribution-common/attribution-settings";
import InfoIcon from "@redotech/redo-icons/info-circle.svg";
import {
  analyticsFiltersFromSettings,
  FILTERABLE_EVIDENCE,
  type AnalyticsAttributionFilters,
} from "@redotech/redo-marketing-common/analytics-attribution-filters";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { useTeamFeatureFlag } from "@redotech/redo-merchant-app-common/use-team-feature-flag";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { createTabConfig, useTabs } from "@redotech/redo-web/utils/use-tabs";
import { useSearchParams } from "react-router-dom";
import { defaultAnalyticsDateFilter } from "./analytics-date-range";
import {
  evidenceForChannel,
  MarketingAttributionEventsFilter,
} from "./filters/marketing-attribution-events-filter";
import { MarketingAttributionFilter } from "./filters/marketing-attribution-filter";
import { BotFilter, MarketingBotFilter } from "./filters/marketing-bot-filter";
import {
  ChannelFilter,
  MarketingChannelFilter,
} from "./filters/marketing-channel-filter";
import { MarketingDateRangeFilter } from "./filters/marketing-date-range-filter";
import { MarketingAnalyticsAudience } from "./marketing-analytics-sections/marketing-analytics-audience";
import { MarketingAnalyticsBackInStock } from "./marketing-analytics-sections/marketing-analytics-back-in-stock";
import { MarketingAnalyticsOverview } from "./marketing-analytics-sections/marketing-analytics-overview";
import { MarketingAnalyticsPerformance } from "./marketing-analytics-sections/marketing-analytics-performance";

export enum MarketingAnalyticsPage {
  OVERVIEW = "overview",
  AUDIENCE = "audience",
  BACK_IN_STOCK = "back-in-stock",
  PERFORMANCE = "performance",
}

export const MARKETING_ANALYTICS_TABS_URL_PARAM = "tab";

const tabConfig = createTabConfig(MarketingAnalyticsPage, {
  [MarketingAnalyticsPage.OVERVIEW]: { component: MarketingAnalyticsOverview },
  [MarketingAnalyticsPage.AUDIENCE]: { component: MarketingAnalyticsAudience },
  [MarketingAnalyticsPage.BACK_IN_STOCK]: {
    component: MarketingAnalyticsBackInStock,
  },
  [MarketingAnalyticsPage.PERFORMANCE]: {
    component: MarketingAnalyticsPerformance,
  },
});

const TABS_WITH_OWN_FILTERS: readonly MarketingAnalyticsPage[] = [
  MarketingAnalyticsPage.BACK_IN_STOCK,
  MarketingAnalyticsPage.PERFORMANCE,
];

/** The mart splits clicks by bot verdict and nothing else, so the filter must
 *  say what it did not touch. */
const BOT_FILTER_SCOPE_NOTE =
  "Opens, sends, orders, and revenue on this page still count all activity.";

/** For a failed settings read, so the page still shows. */
const FALLBACK_FILTERS: AnalyticsAttributionFilters = {
  attributionDays: undefined,
  attributionEvidence: [...FILTERABLE_EVIDENCE],
};

export const MarketingAnalytics = memo(function MarketingAnalytics() {
  const { data: attributionSettings, isPending: settingsPending } = useRpcQuery(
    RedoMarketingRpcClientContext,
    "getAttributionSettings",
    {},
  );
  // Settings supply the starting values only; an edit below takes over.
  const startingFilters = settingsPending
    ? undefined
    : attributionSettings
      ? analyticsFiltersFromSettings(attributionSettings)
      : FALLBACK_FILTERS;
  const [editedFilters, setEditedFilters] =
    useState<AnalyticsAttributionFilters>();
  const filters = editedFilters ?? startingFilters;

  const [channelFilter, setChannelFilter] = useState<ChannelFilter>("all");
  const [botFilter, setBotFilter] = useState<BotFilter>("all");
  const selectedEvidence = filters?.attributionEvidence ?? [];
  // Hiding by channel is computed, not saved, so switching back to all
  // channels brings the hidden checkboxes back.
  const visibleEvidence = evidenceForChannel(channelFilter);
  const attributionEvidence = selectedEvidence.filter((evidenceType) =>
    visibleEvidence.includes(evidenceType),
  );
  // The filter only reports checkboxes this channel shows, so keep the hidden
  // ones instead of treating them as unchecked.
  const selectVisibleEvidence = (evidence: AttributionEvidenceType[]) =>
    filters &&
    setEditedFilters({
      ...filters,
      attributionEvidence: [
        ...evidence,
        ...filters.attributionEvidence.filter(
          (evidenceType) => !visibleEvidence.includes(evidenceType),
        ),
      ],
    });
  const setAttributionDays = (attributionDays: number | undefined) =>
    filters && setEditedFilters({ ...filters, attributionDays });

  const [dateFilter, setDateFilter] = useState<
    [Temporal.PlainDate, Temporal.PlainDate] | undefined
  >(defaultAnalyticsDateFilter);

  const performanceFlag = useTeamFeatureFlag(
    MarketingFlagKey.MARKETING_PERFORMANCE_REPORT,
  );
  const [searchParams] = useSearchParams();
  // A reload on the tab keeps it while the flag loads, instead of bouncing to
  // Overview. Everyone else waits for the flag before the tab appears.
  const showPerformance =
    performanceFlag.enabled ||
    (performanceFlag.pending &&
      searchParams.get(MARKETING_ANALYTICS_TABS_URL_PARAM) ===
        MarketingAnalyticsPage.PERFORMANCE);
  const filterTabs = useCallback(
    (tab: MarketingAnalyticsPage) =>
      tab !== MarketingAnalyticsPage.PERFORMANCE || showPerformance,
    [showPerformance],
  );
  const { selectedTab, setSelectedTab, tabs, SelectedTabComponent } = useTabs({
    tabConfig,
    filterTabs,
    urlParam: MARKETING_ANALYTICS_TABS_URL_PARAM,
  });
  const hasOwnFilters = TABS_WITH_OWN_FILTERS.includes(selectedTab);

  return (
    <Flex
      dir="column"
      gap={
        selectedTab === MarketingAnalyticsPage.BACK_IN_STOCK ? "none" : "4xl"
      }
      w="full"
    >
      <TabsPortal>
        <RedoHorizontalTabs<MarketingAnalyticsPage>
          drawBottomLine={false}
          pl="none"
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          size="xs"
          tabs={tabs}
        />
      </TabsPortal>
      {!filters ? (
        <Flex justify="center">
          <RedoSpinner />
        </Flex>
      ) : (
        <>
          {!hasOwnFilters && (
            <Flex dir="column" gap="md">
              <Flex dir="row" gap="md">
                {selectedTab === MarketingAnalyticsPage.OVERVIEW && (
                  <>
                    <MarketingAttributionFilter
                      attributionFilter={filters.attributionDays}
                      setAttributionFilter={setAttributionDays}
                    />
                    <MarketingChannelFilter
                      channelFilter={channelFilter}
                      setChannelFilter={setChannelFilter}
                    />
                    <MarketingBotFilter
                      botFilter={botFilter}
                      setBotFilter={setBotFilter}
                    />
                    <MarketingAttributionEventsFilter
                      channelFilter={channelFilter}
                      onChange={selectVisibleEvidence}
                      selectedEvidence={attributionEvidence}
                    />
                  </>
                )}
                {selectedTab === MarketingAnalyticsPage.AUDIENCE && (
                  <MarketingChannelFilter
                    channelFilter={channelFilter}
                    setChannelFilter={setChannelFilter}
                  />
                )}
                <MarketingDateRangeFilter
                  dateFilter={dateFilter}
                  setDateFilter={setDateFilter}
                />
              </Flex>
              {selectedTab === MarketingAnalyticsPage.OVERVIEW &&
                botFilter !== "all" && (
                  <RedoAlert
                    color="gray"
                    description={BOT_FILTER_SCOPE_NOTE}
                    Icon={InfoIcon}
                    size="sm"
                    title="Bot filter applies to click metrics only"
                  />
                )}
            </Flex>
          )}
          <SelectedTabComponent
            attributionDays={filters.attributionDays}
            attributionEvidence={attributionEvidence}
            botFilter={botFilter}
            channelFilter={channelFilter}
            dateFilter={dateFilter}
          />
        </>
      )}
    </Flex>
  );
});
